import { useState, useEffect, useRef, useCallback } from 'react';
import DashboardLayout from '@/components/DashboardLayout';
import { useAuth } from '@/context/AuthContext';
import { useTheme } from '@/context/ThemeContext';
import { marketAPI, tradingAPI, spreadAPI } from '@/lib/api';
import { useWebSocket } from '@/hooks/useWebSocket';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { ArrowUpRight, ArrowDownRight, Search, X, ShieldAlert } from 'lucide-react';
import { toast } from 'sonner';
import { Link } from 'react-router-dom';

const formatCurrency = (v) => v == null ? '$0.00' : new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(v);
const TIMEFRAMES = ['1', '5', '15', '60', '240', 'D'];
const TF_LABELS = { '1': '1m', '5': '5m', '15': '15m', '60': '1h', '240': '4h', 'D': '1D' };

const MARKET_TABS = [
  { key: 'crypto', label: 'CRYPTO' },
  { key: 'currency', label: 'FOREX' },
  { key: 'indices', label: 'INDICES' },
  { key: 'commodity', label: 'COMMODITY' },
  { key: 'stocks', label: 'STOCKS' },
];

function TradingViewChart({ symbol, interval, chartTheme }) {
  const containerRef = useRef(null);
  useEffect(() => {
    if (!containerRef.current) return;
    containerRef.current.innerHTML = '';
    const script = document.createElement('script');
    script.src = 'https://s3.tradingview.com/tv.js';
    script.async = true;
    const isDark = chartTheme === 'dark';
    script.onload = () => {
      if (window.TradingView && containerRef.current) {
        new window.TradingView.widget({
          container_id: containerRef.current.id,
          autosize: true,
          symbol: symbol || 'BINANCE:BTCUSDT',
          interval: interval || '15',
          timezone: 'Asia/Kolkata',
          theme: isDark ? 'dark' : 'light',
          style: '1',
          locale: 'en',
          toolbar_bg: isDark ? '#0A0A0A' : '#FFFFFF',
          enable_publishing: false,
          allow_symbol_change: true,
          hide_top_toolbar: false,
          hide_side_toolbar: false,
          withdateranges: true,
          studies: ['RSI@tv-basicstudies', 'MACD@tv-basicstudies'],
          backgroundColor: isDark ? '#0A0A0A' : '#FFFFFF',
          gridColor: isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.06)',
        });
      }
    };
    document.head.appendChild(script);
    return () => { if (script.parentNode) script.parentNode.removeChild(script); };
  }, [symbol, interval, chartTheme]);
  return <div id="tv_chart_container" ref={containerRef} className="w-full h-full min-h-[400px]" />;
}

export default function TradingPage() {
  const { user, refreshUser } = useAuth();
  const { theme } = useTheme();
  const { prices: wsPrices } = useWebSocket();
  const [marketTab, setMarketTab] = useState('crypto');
  const [coins, setCoins] = useState([]);
  const [otherMarkets, setOtherMarkets] = useState({});
  const [positions, setPositions] = useState([]);
  const [selectedCoin, setSelectedCoin] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [timeframe, setTimeframe] = useState('15');
  const [tradeType, setTradeType] = useState('buy');
  const [volume, setVolume] = useState('');
  const [stopLoss, setStopLoss] = useState('');
  const [takeProfit, setTakeProfit] = useState('');
  const [loading, setLoading] = useState(false);
  const [closingId, setClosingId] = useState(null);
  const [tradingAccount, setTradingAccount] = useState(null);
  const [showTradeModal, setShowTradeModal] = useState(false);
  const [userSpreadPercent, setUserSpreadPercent] = useState(0);
  const hasInitialSelection = useRef(false);

  const loadData = useCallback(async () => {
    try {
      const [coinsRes, posRes, catRes, accRes] = await Promise.all([
        marketAPI.getTopCoins(20), tradingAPI.getPositions(), marketAPI.getCategories(), tradingAPI.getAccount()
      ]);
      // Only set coins on first load (fast poll handles live updates after)
      if (!hasInitialSelection.current) {
        setCoins(coinsRes.data);
        if (coinsRes.data.length > 0) {
          hasInitialSelection.current = true;
          setSelectedCoin(coinsRes.data[0]);
        }
      }
      setPositions(posRes.data);
      setOtherMarkets(catRes.data);
      if (accRes.data?.account_number) setTradingAccount(accRes.data);
    } catch (e) { console.error(e); }
  }, []);

  // Initial load + slow refresh for positions/account (every 10s)
  useEffect(() => { loadData(); const i = setInterval(loadData, 10000); return () => clearInterval(i); }, [loadData]);

  // Fast price poll every 1.5s for real-time market data (crypto + forex/commodity/stocks)
  useEffect(() => {
    const pollPrices = async () => {
      try {
        const [cryptoRes, catRes] = await Promise.all([
          marketAPI.getTopCoins(20),
          marketAPI.getCategories(),
        ]);
        if (cryptoRes.data?.length > 0) {
          const priceMap = {};
          cryptoRes.data.forEach(p => {
            if (p.id) priceMap[p.id] = p;
            if (p.symbol) priceMap[p.symbol.toLowerCase()] = p;
          });
          setCoins(prev => {
            if (prev.length === 0) return cryptoRes.data;
            return prev.map(c => {
              const update = priceMap[c.id] || priceMap[c.symbol?.toLowerCase()];
              return update ? { ...c, ...update, id: c.id, symbol: c.symbol, name: c.name, image: c.image } : c;
            });
          });
          setSelectedCoin(prev => {
            if (!prev) return prev;
            const update = priceMap[prev.id] || priceMap[prev.symbol?.toLowerCase()];
            return update ? { ...prev, ...update, id: prev.id, symbol: prev.symbol, name: prev.name, image: prev.image } : prev;
          });
        }
        if (catRes.data) {
          setOtherMarkets(catRes.data);
          // Also update selectedCoin if it's from a non-crypto market
          setSelectedCoin(prev => {
            if (!prev || prev.image) return prev; // crypto has image, skip
            for (const items of Object.values(catRes.data)) {
              const match = items.find(i => i.id === prev.id || i.symbol === prev.symbol);
              if (match) return { ...prev, ...match };
            }
            return prev;
          });
        }
      } catch { /* silent */ }
    };
    const i = setInterval(pollPrices, 1500);
    return () => clearInterval(i);
  }, []);
  useEffect(() => { spreadAPI.getStatus().then(r => setUserSpreadPercent(r.data?.default_spread_percent || 0)).catch(() => {}); }, []);

  // Merge WebSocket real-time prices into coins, otherMarkets, and selectedCoin
  useEffect(() => {
    if (!wsPrices || wsPrices.length === 0) return;
    const priceMap = {};
    wsPrices.forEach(p => {
      if (p.id) priceMap[p.id] = p;
      if (p.symbol) priceMap[p.symbol.toLowerCase()] = p;
    });
    // Update crypto coins
    setCoins(prev => prev.map(c => {
      const update = priceMap[c.id] || priceMap[c.symbol?.toLowerCase()];
      return update ? { ...c, ...update, id: c.id, symbol: c.symbol, name: c.name, image: c.image } : c;
    }));
    // Update other markets (currency, indices, commodities)
    setOtherMarkets(prev => {
      const updated = {};
      for (const [cat, items] of Object.entries(prev)) {
        updated[cat] = items.map(c => {
          const update = priceMap[c.id] || priceMap[c.symbol?.toLowerCase()];
          return update ? { ...c, ...update, id: c.id, symbol: c.symbol, name: c.name } : c;
        });
      }
      return updated;
    });
    // Update selected coin
    if (selectedCoin) {
      const update = priceMap[selectedCoin.id] || priceMap[selectedCoin.symbol?.toLowerCase()];
      if (update) setSelectedCoin(prev => ({ ...prev, ...update, id: prev.id, symbol: prev.symbol, name: prev.name, image: prev.image }));
    }
  }, [wsPrices]); // eslint-disable-line

  // Get current displayed items based on market tab
  const getDisplayItems = () => {
    if (marketTab === 'crypto') return coins;
    return otherMarkets[marketTab] || [];
  };

  const displayItems = getDisplayItems();
  const filteredItems = displayItems.filter(c =>
    (c.name?.toLowerCase().includes(searchTerm.toLowerCase())) ||
    (c.symbol?.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  const getTvSymbol = () => {
    if (!selectedCoin) return 'BINANCE:BTCUSDT';
    if (selectedCoin.tv_symbol) return selectedCoin.tv_symbol;
    return `BINANCE:${selectedCoin.symbol?.toUpperCase()}USDT`;
  };

  const handleTabChange = (tab) => {
    setMarketTab(tab);
    setSearchTerm('');
    const items = tab === 'crypto' ? coins : (otherMarkets[tab] || []);
    if (items.length > 0) setSelectedCoin(items[0]);
  };

  const handleTrade = async () => {
    if (!selectedCoin || !volume || parseFloat(volume) <= 0) { toast.error('Enter a valid volume'); return; }
    setLoading(true);
    try {
      const res = await tradingAPI.placeOrder({
        symbol: selectedCoin.symbol || selectedCoin.id,
        symbol_name: selectedCoin.name,
        type: tradeType, order_type: 'market', volume: parseFloat(volume),
        price: selectedCoin.current_price,
        stop_loss: stopLoss ? parseFloat(stopLoss) : null,
        take_profit: takeProfit ? parseFloat(takeProfit) : null,
        market_category: marketTab,
      });
      toast.success(`${tradeType.toUpperCase()} order placed for ${selectedCoin.symbol?.toUpperCase()} @ $${res.data.entry_price?.toLocaleString()}`);
      setVolume(''); setStopLoss(''); setTakeProfit('');
      setShowTradeModal(false);
      loadData(); refreshUser();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Trade failed');
    } finally { setLoading(false); }
  };

  const handleClose = async (tradeId) => {
    setClosingId(tradeId);
    try {
      const res = await tradingAPI.closePosition(tradeId);
      toast.success(`Position closed. P/L: ${formatCurrency(res.data.profit_loss)}`);
      loadData(); refreshUser();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to close trade');
    } finally { setClosingId(null); }
  };

  const execPrice = selectedCoin ? selectedCoin.current_price : 0;
  const tradeAmount = selectedCoin && volume ? parseFloat(volume) * execPrice : 0;
  const isCrypto = marketTab === 'crypto';
  const isDemo = user?.account_type === 'demo';
  const kycVerified = isDemo || user?.kyc_status === 'verified' || user?.kyc_status === 'approved';

  const formatPrice = (price, item) => {
    if (!price) return '$0.00';
    const decimals = price < 1 ? 6 : price < 100 ? 4 : 2;
    return price.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: decimals });
  };

  return (
    <DashboardLayout>
      <div className="space-y-4" data-testid="trading-page">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
          {/* Quick Trade Panel — shows first on mobile, third on desktop */}
          <div className="order-first lg:order-last lg:col-span-3 bg-[var(--bg-surface)] border border-[var(--border-subtle)] rounded flex flex-col" data-testid="trade-panel">
            <div className="p-4 border-b border-[var(--border-subtle)]">
              <h3 className="text-sm font-semibold font-['Outfit'] text-[var(--text-primary)]">Quick Trade</h3>
              {selectedCoin && (
                <p className="text-xs text-[var(--text-secondary)] mt-1">{selectedCoin.symbol?.toUpperCase()}/USD • {formatPrice(selectedCoin.current_price, selectedCoin)}</p>
              )}
            </div>
            <div className="flex-1 flex flex-col items-center justify-center p-6 gap-4">
              {selectedCoin && (
                <>
                  <div className="text-center space-y-1">
                    <p className="text-2xl font-bold font-mono text-[var(--text-primary)]">${formatPrice(selectedCoin.current_price, selectedCoin)}</p>
                    <p className={`text-xs font-mono ${(selectedCoin?.price_change_24h || 0) >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                      {(selectedCoin?.price_change_24h || 0) >= 0 ? '+' : ''}{(selectedCoin?.price_change_24h || 0).toFixed(2)}% (24h)
                    </p>
                    {selectedCoin.quote_status && selectedCoin.quote_status !== 'LIVE' && (
                      <span data-testid="quote-status-badge"
                        className={`inline-block px-2 py-0.5 rounded text-[9px] font-semibold tracking-wide ${selectedCoin.quote_status === 'STALE'
                          ? 'bg-amber-500/15 text-amber-400 border border-amber-500/25'
                          : 'bg-red-500/15 text-red-400 border border-red-500/25'}`}>
                        {selectedCoin.quote_status === 'STALE' ? 'DELAYED QUOTE' : 'QUOTE UNAVAILABLE'}
                      </span>
                    )}
                  </div>
                  <div className="grid grid-cols-2 gap-2 w-full text-center">
                    <div className="p-2 rounded bg-emerald-500/5 border border-emerald-500/20">
                      <span className="text-[8px] uppercase text-[var(--text-tertiary)] block">24h High</span>
                      <span className="text-xs font-mono text-emerald-400" data-testid="high-price">{formatPrice(selectedCoin.high_24h || selectedCoin.current_price, selectedCoin)}</span>
                    </div>
                    <div className="p-2 rounded bg-red-500/5 border border-red-500/20">
                      <span className="text-[8px] uppercase text-[var(--text-tertiary)] block">24h Low</span>
                      <span className="text-xs font-mono text-red-400" data-testid="low-price">{formatPrice(selectedCoin.low_24h || selectedCoin.current_price, selectedCoin)}</span>
                    </div>
                     {/* {(() => {
                    const spreadVal = (selectedCoin.ask || selectedCoin.current_price) - (selectedCoin.bid || selectedCoin.current_price);
                    const spreadPct = selectedCoin.current_price > 0 ? (spreadVal / selectedCoin.current_price * 100) : 0;
                    return spreadVal > 0 ? (
                      <div className="flex items-center justify-center gap-2 text-[9px] text-[var(--text-tertiary)]" data-testid="spread-display">
                        <span>Spread: {formatPrice(spreadVal, selectedCoin)}</span>
                        <span>({spreadPct.toFixed(3)}%)</span>
                      </div>
                    ) : null;
                  })()} */}
                  </div>
                  {userSpreadPercent > 0 && (
                    <div className="w-full p-2 rounded bg-amber-500/5 border border-amber-500/20 text-center" data-testid="spread-info">
                      <span className="text-[8px] uppercase text-[var(--text-tertiary)] block">Spread</span>
                      <span className="text-xs font-mono text-amber-400">{userSpreadPercent}%</span>
                    </div>
                  )}
                  {kycVerified ? (
                  <button
                    onClick={() => setShowTradeModal(true)}
                    data-testid="open-trade-modal-btn"
                    className="w-full py-3.5 rounded-lg text-sm font-semibold bg-green-500 hover:bg-green-600 text-white transition-all duration-200 hover:shadow-lg hover:shadow-green-500/20"
                  >
                    Trade {selectedCoin.symbol?.toUpperCase()}
                  </button>
                  ) : (
                  <div className="w-full space-y-2" data-testid="kyc-trade-block">
                    <div className="p-3 bg-amber-500/10 border border-amber-500/20 rounded-lg flex items-start gap-2">
                      <ShieldAlert className="w-4 h-4 text-amber-400 mt-0.5 shrink-0" />
                      <div>
                        <p className="text-xs text-amber-400 font-medium">KYC Required</p>
                        <p className="text-[10px] text-[var(--text-secondary)] mt-0.5">Complete your KYC verification to start trading.</p>
                      </div>
                    </div>
                    <Link to="/profile" data-testid="kyc-verify-link"
                      className="block w-full py-2.5 rounded-lg text-xs font-semibold text-center bg-amber-500/15 text-amber-400 hover:bg-amber-500/25 transition-colors border border-amber-500/30">
                      Complete KYC Verification
                    </Link>
                  </div>
                  )}
                </>
              )}
              {!selectedCoin && (
                <p className="text-sm text-[var(--text-tertiary)]">Select an instrument</p>
              )}

              {tradingAccount && (
                <div className="w-full p-2.5 bg-[var(--bg-primary)] rounded border border-[var(--border-subtle)] space-y-1" data-testid="trading-account-info">
                  <div className="flex justify-between text-[10px]">
                    <span className="text-[var(--text-tertiary)]">Account #{tradingAccount.account_number}</span>
                    <span className={`font-semibold ${tradingAccount.status === 'active' ? 'text-emerald-400' : 'text-red-400'}`}>{tradingAccount.status}</span>
                  </div>
                  <div className="flex justify-between text-[10px]">
                    <span className="text-[var(--text-secondary)]">Balance</span>
                    <span className="font-mono text-[var(--text-primary)]">{formatCurrency(tradingAccount.balance)}</span>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Market Watch */}
          <div className="order-2 lg:order-first lg:col-span-3 bg-[var(--bg-surface)] border border-[var(--border-subtle)] rounded flex flex-col max-h-[calc(100vh-120px)]" data-testid="market-watch">
            {/* Market Category Tabs */}
            <div className="border-b border-[var(--border-subtle)] overflow-x-auto" data-testid="market-category-tabs">
              <div className="flex min-w-max">
                {MARKET_TABS.map((tab) => (
                  <button
                    key={tab.key}
                    onClick={() => handleTabChange(tab.key)}
                    data-testid={`market-tab-${tab.key}`}
                    className={`px-3 py-2.5 text-[10px] font-bold tracking-wider transition-all whitespace-nowrap ${
                      marketTab === tab.key
                        ? 'text-green-400 border-b-2 border-green-400 bg-green-500/5'
                        : 'text-[var(--text-tertiary)] hover:text-[var(--text-secondary)]'
                    }`}
                  >
                    {tab.label}
                  </button>
                ))}
              </div>
            </div>

            <div className="p-3 border-b border-[var(--border-subtle)]">
              <div className="relative">
                <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[var(--text-secondary)]" />
                <input
                  type="text" placeholder="Search pairs..."
                  value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)}
                  data-testid="market-search-input"
                  className="w-full pl-9 pr-8 py-2 bg-[var(--bg-primary)] border border-[var(--border-subtle)] rounded text-sm text-[var(--text-primary)] placeholder:text-[var(--text-tertiary)] focus:outline-none focus:border-green-500"
                />
                {searchTerm && <button onClick={() => setSearchTerm('')} className="absolute right-2 top-1/2 -translate-y-1/2"><X className="w-4 h-4 text-[var(--text-secondary)]" /></button>}
              </div>
            </div>
            <div className="flex-1 overflow-y-auto p-2 space-y-1.5">
              {filteredItems.map((c) => {
                const isUp = (c.price_change_24h || 0) >= 0;
                const isSelected = selectedCoin?.id === c.id;
                return (
                  <button
                    key={c.id}
                    onClick={() => setSelectedCoin(c)}
                    data-testid={`market-coin-${c.symbol}`}
                    className={`w-full p-2.5 rounded-lg text-left transition-all duration-150 border ${
                      isSelected
                        ? 'bg-green-500/10 border-green-500/30'
                        : 'bg-[var(--bg-primary)] border-[var(--border-subtle)] hover:border-green-500/20'
                    }`}
                  >
                    <div className="flex items-center justify-between mb-1.5">
                      <div className="flex items-center gap-2">
                        {isCrypto && c.image && <img src={c.image} alt={c.symbol} className="w-5 h-5 rounded-full" />}
                        <span className="text-xs font-bold font-mono text-[var(--text-primary)]">
                          {isCrypto ? `${c.symbol?.toUpperCase()}/USD` : c.symbol}
                        </span>
                      </div>
                      <span className={`text-[10px] font-mono font-semibold px-1.5 py-0.5 rounded ${isUp ? 'bg-emerald-500/15 text-emerald-400' : 'bg-red-500/15 text-red-400'}`}>
                        {isUp ? '+' : ''}{(c.price_change_24h || 0).toFixed(2)}%
                      </span>
                    </div>
                    <div className="flex items-center justify-between mb-1">
                      <span className={`text-sm font-bold font-mono ${isUp ? 'text-emerald-400' : 'text-red-400'}`}>
                        ${formatPrice(c.current_price, c)}
                      </span>
                    </div>
                    <div className="grid grid-cols-2 gap-x-3 text-[9px]">
                      <div className="flex justify-between">
                        <span className="text-[var(--text-tertiary)]">H:</span>
                        <span className="font-mono text-emerald-400">{formatPrice(c.high_24h || c.current_price * 1.02, c)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-[var(--text-tertiary)]">L:</span>
                        <span className="font-mono text-red-400">{formatPrice(c.low_24h || c.current_price * 0.98, c)}</span>
                      </div>
                    </div>
                    {!isCrypto && (
                      <p className="text-[8px] text-[var(--text-tertiary)] mt-1 truncate">{c.name}</p>
                    )}
                  </button>
                );
              })}
              {filteredItems.length === 0 && (
                <div className="p-4 text-center text-xs text-[var(--text-secondary)]">No instruments found</div>
              )}
            </div>
          </div>

          {/* Chart */}
          <div className="order-3 lg:order-2 lg:col-span-6 bg-[var(--bg-surface)] border border-[var(--border-subtle)] rounded flex flex-col" data-testid="chart-container">
            <div className="px-4 py-3 border-b border-[var(--border-subtle)] flex items-center justify-between">
              <div className="flex items-center gap-3">
                {isCrypto && selectedCoin?.image && <img src={selectedCoin.image} alt="" className="w-6 h-6 rounded-full" />}
                <span className="text-sm font-semibold font-mono text-[var(--text-primary)]">
                  {isCrypto ? `${selectedCoin?.symbol?.toUpperCase() || 'BTC'}/USD` : selectedCoin?.symbol || ''}
                </span>
                <span className="text-lg font-bold font-mono text-[var(--text-primary)]">{selectedCoin ? formatPrice(selectedCoin.current_price, selectedCoin) : ''}</span>
                <span className={`text-xs font-mono ${(selectedCoin?.price_change_24h || 0) >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                  {(selectedCoin?.price_change_24h || 0) >= 0 ? '+' : ''}{(selectedCoin?.price_change_24h || 0).toFixed(2)}%
                </span>
              </div>
              <div className="flex items-center gap-1">
                {TIMEFRAMES.map(tf => (
                  <button key={tf} data-testid={`tf-${tf}`} onClick={() => setTimeframe(tf)} className={`px-2 py-1 rounded text-xs font-mono transition-colors ${timeframe === tf ? 'bg-green-500/20 text-green-400' : 'text-[var(--text-secondary)] hover:text-[var(--text-secondary)]'}`}>
                    {TF_LABELS[tf]}
                  </button>
                ))}
              </div>
            </div>
            <div className="flex-1 min-h-[400px] lg:min-h-[500px]">
              <TradingViewChart symbol={getTvSymbol()} interval={timeframe} chartTheme={theme} />
            </div>
          </div>

        </div>

        {/* Open Positions */}
        {positions.length > 0 && (
          <div className="bg-[var(--bg-surface)] border border-[var(--border-subtle)] rounded" data-testid="trading-positions">
            <div className="px-5 py-3 border-b border-[var(--border-subtle)]">
              <h3 className="text-sm font-semibold font-['Outfit'] text-[var(--text-primary)]">Open Positions ({positions.length})</h3>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-xs text-[var(--text-secondary)] uppercase border-b border-[var(--border-subtle)]">
                    <th className="text-left px-5 py-2.5">Pair</th>
                    <th className="text-left px-3 py-2.5">Type</th>
                    <th className="text-right px-3 py-2.5">Volume</th>
                    <th className="text-right px-3 py-2.5">Entry</th>
                    <th className="text-right px-3 py-2.5">Current</th>
                    <th className="text-right px-3 py-2.5">Target</th>
                    <th className="text-right px-3 py-2.5">SL</th>
                    <th className="text-right px-3 py-2.5">Spread</th>
                    <th className="text-right px-3 py-2.5">P/L</th>
                    <th className="text-right px-5 py-2.5">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {positions.map((p) => (
                    <tr key={p.id} className="border-b border-[var(--border-subtle)] hover:bg-[var(--hover-bg)]">
                      <td className="px-5 py-2.5 font-mono text-[var(--text-primary)] text-xs">{p.symbol?.toUpperCase()}</td>
                      <td className="px-3 py-2.5"><span className={`px-2 py-0.5 rounded text-xs ${p.type === 'buy' ? 'bg-emerald-500/15 text-emerald-400' : 'bg-red-500/15 text-red-400'}`}>{p.type?.toUpperCase()}</span></td>
                      <td className="px-3 py-2.5 text-right font-mono text-[var(--text-secondary)] text-xs">{p.volume}</td>
                      <td className="px-3 py-2.5 text-right font-mono text-[var(--text-secondary)] text-xs">{formatCurrency(p.entry_price)}</td>
                      <td className="px-3 py-2.5 text-right font-mono text-[var(--text-secondary)] text-xs">{formatCurrency(p.current_price)}</td>
                      <td className="px-3 py-2.5 text-right font-mono text-emerald-400/70 text-xs">{p.take_profit ? formatCurrency(p.take_profit) : '-'}</td>
                      <td className="px-3 py-2.5 text-right font-mono text-red-400/70 text-xs">{p.stop_loss ? formatCurrency(p.stop_loss) : '-'}</td>
                      <td className="px-3 py-2.5 text-right font-mono text-amber-400 text-xs">{p.spread_amount ? formatCurrency(p.spread_amount) : '-'}</td>
                      <td className={`px-3 py-2.5 text-right font-mono text-xs font-medium ${(p.profit_loss || 0) >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                        {(p.profit_loss || 0) >= 0 ? '+' : ''}{formatCurrency(p.profit_loss)}
                      </td>
                      <td className="px-5 py-2.5 text-right">
                        <button
                          onClick={() => handleClose(p.id)}
                          disabled={closingId === p.id}
                          data-testid={`close-trade-${p.id}`}
                          className="px-3 py-1 rounded text-xs bg-red-500/15 text-red-400 hover:bg-red-500/25 transition-colors disabled:opacity-50"
                        >
                          {closingId === p.id ? '...' : 'Close'}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      {/* ====== TRADE MODAL ====== */}
      {showTradeModal && selectedCoin && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center px-4 animate-in fade-in duration-200" data-testid="trade-modal-overlay" onClick={() => setShowTradeModal(false)}>
          <div className="bg-[var(--bg-surface)] border border-[var(--border-subtle)] rounded-xl w-full max-w-md p-0 animate-in zoom-in-95 duration-200 shadow-2xl" onClick={e => e.stopPropagation()} data-testid="trade-modal">
            {/* Modal Header */}
            <div className="px-6 pt-5 pb-4 border-b border-[var(--border-subtle)] flex items-center justify-between">
              <div className="flex items-center gap-3">
                {isCrypto && selectedCoin?.image && <img src={selectedCoin.image} alt="" className="w-7 h-7 rounded-full" />}
                <div>
                  <h2 className="text-base font-bold font-['Outfit'] text-[var(--text-primary)]">Trade {selectedCoin.symbol?.toUpperCase()}</h2>
                  <p className="text-xs text-[var(--text-secondary)] font-mono">${formatPrice(selectedCoin.current_price, selectedCoin)}</p>
                </div>
              </div>
              <button onClick={() => setShowTradeModal(false)} className="p-1.5 rounded-lg hover:bg-[var(--hover-bg)] text-[var(--text-secondary)]" data-testid="trade-modal-close">
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Buy / Sell Toggle */}
            <div className="px-6 pt-4">
              <div className="grid grid-cols-2 gap-2 p-1 rounded-lg bg-[var(--bg-primary)] border border-[var(--border-subtle)]">
                <button
                  onClick={() => setTradeType('buy')}
                  data-testid="modal-buy-btn"
                  className={`py-2.5 rounded-md text-sm font-semibold transition-all duration-200 ${tradeType === 'buy' ? 'bg-emerald-500 text-white shadow-lg shadow-emerald-500/20' : 'text-[var(--text-secondary)] hover:text-emerald-400'}`}
                >
                  <ArrowUpRight className="w-4 h-4 inline mr-1" />BUY
                </button>
                <button
                  onClick={() => setTradeType('sell')}
                  data-testid="modal-sell-btn"
                  className={`py-2.5 rounded-md text-sm font-semibold transition-all duration-200 ${tradeType === 'sell' ? 'bg-red-500 text-white shadow-lg shadow-red-500/20' : 'text-[var(--text-secondary)] hover:text-red-400'}`}
                >
                  <ArrowDownRight className="w-4 h-4 inline mr-1" />SELL
                </button>
              </div>
            </div>

            {/* Form */}
            <div className="px-6 py-4 space-y-4">
              <div>
                <Label className="text-xs text-[var(--text-secondary)] mb-1.5 block">Amount (Volume)</Label>
                <Input
                  type="number" step="any" placeholder="Enter volume..." value={volume}
                  onChange={(e) => setVolume(e.target.value)}
                  data-testid="modal-volume-input"
                  className="bg-[var(--bg-primary)] border-[var(--border-subtle)] text-[var(--text-primary)] font-mono placeholder:text-[var(--text-tertiary)] h-11 text-sm"
                  autoFocus
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs text-[var(--text-secondary)] mb-1.5 block">Stop Loss</Label>
                  <Input type="number" step="any" placeholder="Optional" value={stopLoss} onChange={(e) => setStopLoss(e.target.value)}
                    data-testid="modal-stop-loss"
                    className="bg-[var(--bg-primary)] border-[var(--border-subtle)] text-[var(--text-primary)] font-mono placeholder:text-[var(--text-tertiary)] h-9 text-xs" />
                </div>
                <div>
                  <Label className="text-xs text-[var(--text-secondary)] mb-1.5 block">Take Profit</Label>
                  <Input type="number" step="any" placeholder="Optional" value={takeProfit} onChange={(e) => setTakeProfit(e.target.value)}
                    data-testid="modal-take-profit"
                    className="bg-[var(--bg-primary)] border-[var(--border-subtle)] text-[var(--text-primary)] font-mono placeholder:text-[var(--text-tertiary)] h-9 text-xs" />
                </div>
              </div>

              {/* Trade Summary */}
              {volume && parseFloat(volume) > 0 && (
                <div className="p-3 rounded-lg bg-[var(--bg-primary)] border border-[var(--border-subtle)] space-y-2">
                  <div className="flex justify-between text-xs">
                    <span className="text-[var(--text-secondary)]">Execution Price</span>
                    <span className="font-mono text-[var(--text-primary)]">${formatPrice(execPrice, selectedCoin)}</span>
                  </div>
                  {/* <div className="flex justify-between text-xs">
                    <span className="text-[var(--text-secondary)]">Trade Amount</span>
                    <span className="font-mono text-[var(--text-primary)]">{formatCurrency(tradeAmount)}</span>
                  </div> */}
                  {userSpreadPercent > 0 && (
                    <div className="flex justify-between text-xs">
                      <span className="text-amber-400">Spread</span>
                      <span className="font-mono text-amber-400">{userSpreadPercent}%</span>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Confirm Button */}
            <div className="px-6 pb-6">
              <button
                onClick={() => { handleTrade(); }}
                disabled={loading || !volume || parseFloat(volume) <= 0}
                data-testid="modal-confirm-trade"
                className={`w-full py-3.5 rounded-lg text-sm font-semibold transition-all duration-200 disabled:opacity-40 ${
                  tradeType === 'buy'
                    ? 'bg-emerald-500 hover:bg-emerald-600 text-white shadow-lg shadow-emerald-500/20'
                    : 'bg-red-500 hover:bg-red-600 text-white shadow-lg shadow-red-500/20'
                }`}
              >
                {loading ? 'Placing Order...' : `Confirm ${tradeType.toUpperCase()} ${selectedCoin.symbol?.toUpperCase()}`}
              </button>
            </div>
          </div>
        </div>
      )}
    </DashboardLayout>
  );
}
